<script setup lang="ts">
  import { computed, getCurrentInstance, nextTick, ref, watch } from 'vue';
  import PosUiChip from '@/components/pos-v2/ui/PosUiChip.vue';
  import type { TItemFilter } from '@/domain/menu-import/menu-draft';
  import { radioKeyTarget } from '@/domain/store-setup/business-step';

  /**
   * The filter chips of the review (plan §4.5 «Filters»): «Todos 14», «Para
   * revisar 4», «Sin precio 1», «Sin foto 14»; an update offers «Precio
   * nuevo», «Nuevos» and «Sin cambios» instead (`itemFiltersFor`). One
   * choice (a radiogroup: arrow keys move, the active chip is dark); a
   * horizontal scroller on narrow screens.
   */
  const LABEL_KEYS: Readonly<Record<TItemFilter, string>> = {
    all: 'menuImport.filters.all',
    toCheck: 'menuImport.filters.toCheck',
    noPrice: 'menuImport.filters.noPrice',
    noPhoto: 'menuImport.filters.noPhoto',
    priceChanged: 'menuImport.filters.priceChanged',
    new: 'menuImport.filters.new',
    unchanged: 'menuImport.filters.unchanged',
  };

  const props = defineProps<{
    value: TItemFilter;
    counts: Record<TItemFilter, number>;
    /** The chips offered, in order. */
    filters: TItemFilter[];
  }>();
  const emit = defineEmits<{ (e: 'input', filter: TItemFilter): void }>();
  const proxy = getCurrentInstance()!.proxy;
  const root = ref<HTMLElement | null>(null);
  /** The chip holding the Tab stop (roving tabindex). */
  const focusFilter = ref<TItemFilter | null>(null);

  const chips = computed(() =>
    props.filters.map((filter) => ({
      filter,
      label: String(
        proxy.$t(LABEL_KEYS[filter], { count: props.counts[filter] })
      ),
    }))
  );
  const tabStop = computed(() => focusFilter.value ?? props.value);

  function select(filter: TItemFilter) {
    focusFilter.value = filter;
    if (filter !== props.value) emit('input', filter);
  }

  async function onKeydown(event: KeyboardEvent, filter: TItemFilter) {
    const target = radioKeyTarget(
      props.filters.indexOf(filter),
      event.key,
      props.filters.length
    );
    if (target === null) return;
    event.preventDefault();
    const next = props.filters[target];
    focusFilter.value = next;
    await nextTick();
    const chip = root.value?.querySelector(`[data-filter="${next}"]`);
    if (chip instanceof HTMLElement) chip.focus();
  }

  // A filter chosen elsewhere («Revisarlos», a dish opened) takes the Tab stop.
  watch(
    () => props.value,
    (filter) => {
      focusFilter.value = filter;
    }
  );
</script>

<template>
  <div ref="root" class="v2-menu-filter-bar" role="radiogroup">
    <PosUiChip
      v-for="chip in chips"
      :key="chip.filter"
      radio
      :active="chip.filter === value"
      :data-filter="chip.filter"
      :tabindex="chip.filter === tabStop ? 0 : -1"
      @click="select(chip.filter)"
      @keydown.native="onKeydown($event, chip.filter)"
      >{{ chip.label }}</PosUiChip
    >
  </div>
</template>

<style scoped lang="scss">
  .v2-menu-filter-bar {
    @apply -tw-mx-4 tw-flex tw-gap-2 tw-overflow-x-auto tw-px-4 md:tw-mx-0 md:tw-flex-wrap md:tw-overflow-visible md:tw-px-0;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      @apply tw-hidden;
    }
    :deep(.pos-ui-chip) {
      @apply tw-whitespace-nowrap tw-tabular-nums;
    }
  }
</style>
